// contexts/AdminContext.js
import React, { createContext, useContext, useReducer, useEffect, useCallback, useMemo, useRef } from 'react';

// Admin state structure
const initialState = {
  isAuthenticated: false,
  currentUser: null,
  adminToken: null,
  currentPage: 'dashboard',
  loading: false,
  error: null,
  notifications: []
};

// Action types
const AdminActionTypes = {
  LOGIN_START: 'LOGIN_START',
  LOGIN_SUCCESS: 'LOGIN_SUCCESS',
  LOGIN_FAILURE: 'LOGIN_FAILURE',
  LOGOUT: 'LOGOUT',
  SET_CURRENT_PAGE: 'SET_CURRENT_PAGE',
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  CLEAR_ERROR: 'CLEAR_ERROR',
  ADD_NOTIFICATION: 'ADD_NOTIFICATION',
  REMOVE_NOTIFICATION: 'REMOVE_NOTIFICATION'
};

// Reducer function
const adminReducer = (state, action) => {
  switch (action.type) {
    case AdminActionTypes.LOGIN_START:
      return {
        ...state,
        loading: true,
        error: null
      };

    case AdminActionTypes.LOGIN_SUCCESS:
      return {
        ...state,
        isAuthenticated: true,
        currentUser: action.payload.user,
        adminToken: action.payload.token,
        loading: false,
        error: null
      };

    case AdminActionTypes.LOGIN_FAILURE:
      return {
        ...state,
        isAuthenticated: false,
        currentUser: null,
        adminToken: null,
        loading: false,
        error: action.payload
      };

    case AdminActionTypes.LOGOUT:
      return {
        ...initialState
      };

    case AdminActionTypes.SET_CURRENT_PAGE:
      return {
        ...state,
        currentPage: action.payload
      };

    case AdminActionTypes.SET_LOADING:
      return {
        ...state,
        loading: action.payload
      };

    case AdminActionTypes.SET_ERROR:
      return {
        ...state,
        error: action.payload
      };

    case AdminActionTypes.CLEAR_ERROR:
      return {
        ...state,
        error: null
      };

    case AdminActionTypes.ADD_NOTIFICATION:
      return {
        ...state,
        notifications: [...state.notifications, action.payload]
      };

    case AdminActionTypes.REMOVE_NOTIFICATION:
      return {
        ...state,
        notifications: state.notifications.filter(n => n.id !== action.payload)
      };

    default:
      return state;
  }
};


// Create context
const AdminContext = createContext();

// Admin provider component
export const AdminProvider = ({ children }) => {
  const [state, dispatch] = useReducer(adminReducer, initialState);

  // Memoize functions to prevent recreation on every render
  const logout = useCallback(() => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    dispatch({ type: AdminActionTypes.LOGOUT });
  }, []);

  const removeNotification = useCallback((id) => {
    dispatch({
      type: AdminActionTypes.REMOVE_NOTIFICATION,
      payload: id
    });
  }, []);

  const hasCheckedAuth = useRef(false);

useEffect(() => {
  if (!hasCheckedAuth.current) {
    hasCheckedAuth.current = true;
    checkAuthStatus();
  }
}, []);

  const addNotification = useCallback((message, type = 'info') => {
    const notification = {
      id: Date.now(),
      message,
      type,
      timestamp: new Date().toISOString()
    };

    dispatch({
      type: AdminActionTypes.ADD_NOTIFICATION,
      payload: notification
    });
  }, []);

  // Check if user is already authenticated
  const checkAuthStatus = useCallback(() => {
    const adminToken = localStorage.getItem('adminToken');
    const adminUser = localStorage.getItem('adminUser');

    if (adminToken && adminUser) {
      try {
        const user = JSON.parse(adminUser);
        dispatch({
          type: AdminActionTypes.LOGIN_SUCCESS,
          payload: { user, token: adminToken }
        });
      } catch (error) {
        console.error('Error parsing stored auth data:', error);
        logout();
      }
    }
  }, [logout]);

  // Check for existing auth on mount (only run once)
  useEffect(() => {
    checkAuthStatus();
  }, []);

  // Auto-remove notifications after 5 seconds
  useEffect(() => {
    if (state.notifications.length > 0) {
      const latestNotification = state.notifications[state.notifications.length - 1];
      const timer = setTimeout(() => {
        removeNotification(latestNotification.id);
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [state.notifications.length, removeNotification]); // Only depend on length, not the whole array

  // Login function
  const login = useCallback(async (email, password) => {
    dispatch({ type: AdminActionTypes.LOGIN_START });

    try {
      const response = await fetch('https://phenixloc-api.fnvt.net/api/admin/auth/admin-login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Store authentication data
        localStorage.setItem('adminToken', data.token);
        localStorage.setItem('adminUser', JSON.stringify(data.user));

        dispatch({
          type: AdminActionTypes.LOGIN_SUCCESS,
          payload: { user: data.user, token: data.token }
        });

        addNotification(`Welcome back, ${data.user.username}!`, 'success');
        return { success: true };
      } else {
        throw new Error(data.message || 'Authentication failed');
      }
    } catch (error) {
      dispatch({
        type: AdminActionTypes.LOGIN_FAILURE,
        payload: error.message
      });
      addNotification(error.message, 'error');
      return { success: false, error: error.message };
    }
  }, [addNotification]);

  // Set current page
  const setCurrentPage = useCallback((page) => {
    dispatch({
      type: AdminActionTypes.SET_CURRENT_PAGE,
      payload: page
    });
  }, []);

  // Set loading state
  const setLoading = useCallback((loading) => {
    dispatch({
      type: AdminActionTypes.SET_LOADING,
      payload: loading
    });
  }, []);

  // Set error state
  const setError = useCallback((error) => {
    dispatch({
      type: AdminActionTypes.SET_ERROR,
      payload: error
    });
  }, []);

  // Clear error state
  const clearError = useCallback(() => {
    dispatch({ type: AdminActionTypes.CLEAR_ERROR });
  }, []);

  // API helper function
  const apiCall = useCallback(async (url, options = {}) => {
    try {
      // Replace the base URL for local development
      const fullUrl = url;
      
      const currentToken = localStorage.getItem('adminToken'); // Get fresh token each time
    const defaultOptions = {
      headers: {
        'Content-Type': 'application/json',
        ...(currentToken && { 'Authorization': `Bearer ${currentToken}` })
      }
    };

      const response = await fetch(fullUrl, {
        ...defaultOptions,
        ...options,
        headers: {
          ...defaultOptions.headers,
          ...options.headers
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'API request failed');
      }

      return await response.json();
    } catch (error) {
      setError(error.message);
      throw error;
    }
  }, [setError]);

  // Memoize the context value to prevent unnecessary re-renders
  const value = useMemo(() => ({
    ...state,
    login,
    logout,
    setCurrentPage,
    addNotification,
    removeNotification,
    setLoading,
    setError,
    clearError,
    apiCall
  }), [
    state,
    login,
    logout,
    setCurrentPage,
    addNotification,
    removeNotification,
    setLoading,
    setError,
    clearError,
    apiCall
  ]);

  return (
    <AdminContext.Provider value={value}>
      {children}
    </AdminContext.Provider>
  );
};

// Custom hook to use admin context
export const useAdmin = () => {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error('useAdmin must be used within an AdminProvider');
  }
  return context;
};

export default AdminContext;